import { ArrowUpRight, Linkedin, Instagram, Youtube, Facebook } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { EMAIL, SKUNK_URL } from "@/data";
import { MaskLineInView } from "@/components/Reveal";

const socials = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/cerra-design/about/", Icon: Linkedin },
  { label: "Instagram", href: "https://www.instagram.com/cerradesign_studio", Icon: Instagram },
  { label: "Facebook", href: "https://www.facebook.com/cerradesign", Icon: Facebook },
  { label: "YouTube", href: "https://www.youtube.com/@CerraDesign", Icon: Youtube },
];

const Footer = () => {
  const { pathname } = useLocation();
  const isContactPage = pathname === "/contact";
  return (
  <footer
    id="contact"
    data-testid="footer"
    className="bg-white text-[var(--cerra-fg)] border-t border-[var(--cerra-border)]"
  >
    <div className="mx-auto max-w-[1600px] px-5 sm:px-8 pt-24 sm:pt-32 pb-12">
      <span className="text-[var(--orange)] text-xs font-bold uppercase tracking-[0.28em]">
        ✦ Contact
      </span>

      <div className="mt-8 font-head font-black uppercase kern-tight text-3xl sm:text-5xl lg:text-6xl leading-[0.95]">
        <MaskLineInView>Let's build</MaskLineInView>
        <MaskLineInView delay={0.1}>something engineered.</MaskLineInView>
      </div>

      <p className="mt-12 mb-4 text-center font-head text-4xl sm:text-6xl lg:text-7xl font-medium text-black/50 kern-tight leading-tight">
        Have a project?{" "}
        <span className="text-[var(--cerra-fg)]">Let's build it.</span>
      </p>

      <div className="mb-4 flex justify-center">
        {!isContactPage && (
          <Link
            data-testid="footer-contact-cta"
            to="/contact"
            className="group inline-flex items-center gap-3 bg-[var(--orange)] hover:bg-[var(--orange-hover)] text-white text-xs font-bold uppercase tracking-[0.22em] px-10 py-5 transition-colors duration-300"
          >
            Start an Enquiry
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        )}
      </div>

      <div className="mt-16 grid gap-12 md:grid-cols-12 border-t border-[var(--cerra-border)] pt-12">
        {/* Email */}
        <div className="md:col-span-12 lg:col-span-4">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/40">
            Studio Enquiries
          </p>
          <a
            data-testid="footer-email"
            href={`mailto:${EMAIL}`}
            className="group mt-4 inline-flex items-center gap-3 font-head text-lg sm:text-2xl lg:text-3xl font-bold whitespace-nowrap hover:text-[var(--orange)] transition-colors duration-300"
          >
            {EMAIL}
            <ArrowUpRight
              size={26}
              className="shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.22em] text-[var(--orange)]">
            Advanced Design · Engineered Craft
          </p>
          <p className="mt-3 max-w-md text-sm sm:text-base leading-relaxed text-black/60">
            A global design studio working across industrial, vehicle and
            motorcycle disciplines.
          </p>
        </div>

        {/* Studio address */}
        <div className="col-span-6 md:col-span-6 lg:col-span-2" data-testid="footer-studio">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/40">
            Studio
          </p>
          <address className="mt-4 not-italic text-sm leading-relaxed text-black/70">
            Cerra Design HQ<br />
            2/12 Aileen Avenue<br />
            Heidelberg West, Australia<br />
            3081
          </address>
        </div>

        {/* Contact details */}
        <div className="col-span-6 md:col-span-6 lg:col-span-2" data-testid="footer-contact">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/40">
            Contact
          </p>
          <ul className="mt-4 space-y-2 text-sm text-black/70">
            <li>
              <a
                data-testid="footer-contact-email"
                href="mailto:studio@cerradesign.com"
                className="hover:text-[var(--orange)] transition-colors duration-300"
              >
                studio@cerradesign.com
              </a>
            </li>
            <li>
              <a
                data-testid="footer-contact-phone"
                href="tel:+61407350408"
                className="hover:text-[var(--orange)] transition-colors duration-300"
              >
                +61 (0) 407 350 408
              </a>
            </li>
            <li className="text-black/50">Mon – Fri: 8:30 – 17:30</li>
          </ul>
        </div>

        {/* Social */}
        <div className="col-span-6 md:col-span-6 lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/40">
            Social
          </p>
          <ul className="mt-4 space-y-2">
            {socials.map((s) => (
              <li key={s.label}>
                <a
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 text-sm font-medium hover:text-[var(--orange)] transition-colors duration-300"
                >
                  <s.Icon size={16} className="text-black/50 group-hover:text-[var(--orange)] transition-colors duration-300" />
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Sub brand */}
        <div className="col-span-6 md:col-span-6 lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/40">
            Sub-Brand
          </p>
          <a
            data-testid="footer-skunk-link"
            href={SKUNK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.1em] hover:text-[var(--orange)] transition-colors duration-300"
          >
            Visit Skunk Machine
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <p className="mt-3 text-xs text-black/50 leading-relaxed">
            Motorcycle builds &amp; parts.
          </p>
        </div>
      </div>

      <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[var(--cerra-border)] pt-8">
        <img
          src="/cerra-logo-white.png"
          alt="Cerra Design"
          className="h-5 w-auto"
          style={{ filter: "invert(1)" }}
        />
        <p className="text-[11px] uppercase tracking-[0.2em] text-black/40">
          © {new Date().getFullYear()} Cerra Design — Advanced Design. Engineered Craft.
        </p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
